import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatMoney } from '@/shared/lib/format/money';
import { Spinner, EmptyMsg } from '../WalletPagePrimitives';
import { CHART_FILTERS, selectCls } from '../walletPage.constants';
import { fmtDate } from '../walletPage.utils';
import type { ChartPoint } from '../WalletPage.types';

type WalletChartProps = {
  chartData: ChartPoint[];
  isLoading: boolean;
  chartReason: string;
  onReasonChange: (value: string) => void;
  chartStart: string;
  onStartChange: (value: string) => void;
  chartEnd: string;
  onEndChange: (value: string) => void;
};

export function WalletChart({
  chartData,
  isLoading,
  chartReason,
  onReasonChange,
  chartStart,
  onStartChange,
  chartEnd,
  onEndChange,
}: WalletChartProps) {
  return (
    <div className="mb-5 rounded-xl border border-[var(--dash-line)] bg-white">
      <div className="flex flex-col gap-3 border-b border-[var(--dash-line)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="text-left">
          <h2 className="text-sm font-semibold text-[var(--dash-ink)]">Wallet usage</h2>
          <p className="text-xs text-[var(--dash-muted)]">Spending over time</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={chartReason}
            onChange={(e) => onReasonChange(e.target.value)}
            className={selectCls}
          >
            {CHART_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={chartStart}
            onChange={(e) => onStartChange(e.target.value)}
            className={selectCls}
          />
          <span className="text-xs text-[var(--dash-muted)]">–</span>
          <input
            type="date"
            value={chartEnd}
            max={fmtDate(new Date())}
            onChange={(e) => onEndChange(e.target.value)}
            className={selectCls}
          />
        </div>
      </div>

      <div className="px-2 py-4 sm:px-4">
        {isLoading ? (
          <Spinner />
        ) : chartData.length === 0 ? (
          <EmptyMsg text="No data for the selected period." />
        ) : (
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F6" />
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#94A3B8', fontSize: 11 }}
                  tickMargin={10}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: '#94A3B8', fontSize: 11 }}
                  width={58}
                  tickFormatter={(v: number) =>
                    v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)
                  }
                />
                <ReTooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid #E2E8F0',
                    fontSize: 12,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                  }}
                  formatter={(v) => [formatMoney(Number(v ?? 0)), 'Amount']}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="var(--dash-brand, #003481)"
                  strokeWidth={2}
                  dot={{ r: 3.5, fill: 'var(--dash-brand, #003481)', strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
